import { useState, useCallback, useRef, useEffect } from 'react';

const DEFAULT_VIDEO_WIDTH = 55;
const DEFAULT_CONTENT_HEIGHT = 60;
const MIN_EVENTS_PERCENT = 25;
const MAX_CONTENT_HEIGHT = 100 - MIN_EVENTS_PERCENT;

export const useResizeHandlers = (appInfoExpanded) => {
  const [videoWidth, setVideoWidth] = useState(DEFAULT_VIDEO_WIDTH);
  const [contentHeight, setContentHeight] = useState(DEFAULT_CONTENT_HEIGHT);

  const containerRef = useRef(null);
  const isDraggingVertical = useRef(false);
  const isDraggingHorizontal = useRef(false);
  const appInfoExpandedRef = useRef(appInfoExpanded);
  appInfoExpandedRef.current = appInfoExpanded;

  const handleVerticalMouseUp = useCallback(() => {
    isDraggingVertical.current = false;
    document.removeEventListener('mousemove', handleVerticalMouseMove);
    document.removeEventListener('mouseup', handleVerticalMouseUp);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVerticalMouseMove = useCallback((e) => {
    if (!isDraggingVertical.current || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const appInfoHeight = appInfoExpandedRef.current ? 120 : 60;
    const availableHeight = rect.height - appInfoHeight;
    const newPercentage = ((e.clientY - rect.top - appInfoHeight) / availableHeight) * 100;
    const constrainedPercentage = Math.max(20, Math.min(MAX_CONTENT_HEIGHT, newPercentage));
    setContentHeight(constrainedPercentage);
  }, []);

  const handleVerticalMouseDown = useCallback((e) => {
    e.preventDefault();
    isDraggingVertical.current = true;
    document.addEventListener('mousemove', handleVerticalMouseMove);
    document.addEventListener('mouseup', handleVerticalMouseUp);
  }, [handleVerticalMouseMove, handleVerticalMouseUp]);

  const handleHorizontalMouseUp = useCallback(() => {
    isDraggingHorizontal.current = false;
    document.removeEventListener('mousemove', handleHorizontalMouseMove);
    document.removeEventListener('mouseup', handleHorizontalMouseUp);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleHorizontalMouseMove = useCallback((e) => {
    if (!isDraggingHorizontal.current || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const availableWidth = rect.width;
    const newPercentage = ((e.clientX - rect.left) / availableWidth) * 100;
    
    const constrainedPercentage = Math.max(20, Math.min(80, newPercentage));
    setVideoWidth(constrainedPercentage);
  }, []);

  const handleHorizontalMouseDown = useCallback((e) => {
    e.preventDefault();
    isDraggingHorizontal.current = true;
    document.addEventListener('mousemove', handleHorizontalMouseMove);
    document.addEventListener('mouseup', handleHorizontalMouseUp);
  }, [handleHorizontalMouseMove, handleHorizontalMouseUp]);

  // Clean up document listeners if component unmounts while dragging
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleVerticalMouseMove);
      document.removeEventListener('mouseup', handleVerticalMouseUp);
      document.removeEventListener('mousemove', handleHorizontalMouseMove);
      document.removeEventListener('mouseup', handleHorizontalMouseUp);
    };
  // All four handlers are stable ([] deps), so this effect never re-runs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    containerRef,
    videoWidth,
    contentHeight,
    setVideoWidth,
    setContentHeight,
    handleVerticalMouseDown,
    handleHorizontalMouseDown
  };
};
